import { useState, useRef, useCallback, useEffect } from 'react';
import { ChevronLeft, Mic, MicOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import ProgressDots from './ProgressDots';
import {
  STARTER_CHIPS,
  ONBOARDING_EVENTS,
  getMemoryPlaceholder,
} from '@/constants/onboardingConfig';
import { API } from '@/lib/axios';

const MIN_CHARS = 10;
const canRecord = typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

export default function Screen3Memory({
  onNext,
  onBack,
  memoryInput,
  onMemoryChange,
  intent,
  audience,
  sessionId,
  trackEvent,
}) {
  const [chipIndex, setChipIndex] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const textareaRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const charCount = memoryInput.length;
  const isReady = charCount >= MIN_CHARS;
  const placeholder = getMemoryPlaceholder(intent, audience);

  // Auto-resize textarea
  const resizeTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [memoryInput, resizeTextarea]);

  const handleChange = (e) => {
    onMemoryChange(e.target.value);
  };

  const handleChipClick = (chip) => {
    const newVal = memoryInput ? `${memoryInput} ${chip}` : chip;
    onMemoryChange(newVal);
    textareaRef.current?.focus();
    trackEvent(ONBOARDING_EVENTS.CHIP_CLICKED, { chip });
  };

  const handleSurpriseMe = () => {
    const next = (chipIndex + 1) % STARTER_CHIPS.length;
    setChipIndex(next);
    trackEvent(ONBOARDING_EVENTS.SURPRISE_ME);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : '';
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      chunksRef.current = [];
      recorder.ondataavailable = (e) => chunksRef.current.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setIsRecording(false);
        setIsTranscribing(true);
        try {
          const actualMime = recorder.mimeType || mimeType || 'audio/webm';
          const ext = actualMime.includes('mp4') ? 'm4a' : 'webm';
          const blob = new Blob(chunksRef.current, { type: actualMime });
          const formData = new FormData();
          formData.append('file', blob, `recording.${ext}`);
          const token = localStorage.getItem('auth_token');
          const res = await fetch(`${API}/onboarding/transcribe`, {
            method: 'POST',
            credentials: 'include',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: formData,
          });
          if (!res.ok) throw new Error('Transcription failed');
          const data = await res.json();
          if (data.text) {
            const joined = memoryInput ? `${memoryInput} ${data.text}` : data.text;
            onMemoryChange(joined);
          }
        } catch {
          toast.error('Could not transcribe. Please type your memory instead.');
        } finally {
          setIsTranscribing(false);
        }
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      trackEvent(ONBOARDING_EVENTS.VOICE_CLICKED);
    } catch {
      toast.error('Microphone access denied. Please type your memory instead.');
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
  };

  const handleSubmit = () => {
    if (!isReady) return;
    trackEvent(ONBOARDING_EVENTS.MEMORY_SUBMITTED, { char_count: charCount });
    onNext();
  };

  return (
    <div className="story-page flex flex-col" style={{ height: '100dvh', minHeight: '100vh' }}>
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-6 pt-6 pb-4 max-w-md mx-auto w-full">
        <div className="flex items-center mb-6">
          <button
            onClick={onBack}
            className="p-2 -ml-2 text-[#8C847A] dark:text-[#7C746A] hover:text-[#2A2420] dark:hover:text-[#F0EBE4] transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        </div>

        <ProgressDots current={2} />

        <h2 className="story-title text-2xl sm:text-3xl mb-2">
          Share one memory.
        </h2>
        <p className="story-meta mb-5">
          Write it as you'd say it out loud. Don't worry about structure.
        </p>

        {/* Starter chip */}
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => handleChipClick(STARTER_CHIPS[chipIndex])}
            className="story-pill hover:border-[var(--story-primary)]"
          >
            {STARTER_CHIPS[chipIndex]}
          </button>
          <button
            onClick={handleSurpriseMe}
            className="text-xs text-[#A09890] dark:text-[#5C5650] hover:text-[#5C4D6B] dark:hover:text-[#B0A0C0] transition-colors whitespace-nowrap"
          >
            Try another →
          </button>
        </div>

        {/* Textarea */}
        <div className="relative mb-2">
          <textarea
            ref={textareaRef}
            value={memoryInput}
            onChange={handleChange}
            placeholder={placeholder}
            rows={3}
            className="w-full resize-none overflow-hidden rounded-2xl border border-[var(--story-line)] bg-[var(--story-surface)] px-4 py-3 text-base text-[var(--story-ink)] placeholder:text-[var(--story-faint)] font-serif leading-relaxed focus:outline-none focus:border-[var(--story-primary)] transition-colors"
            style={{ minHeight: '120px' }}
          />
        </div>

        {/* Progress bar + char count */}
        <div className="flex items-center justify-between mb-4">
          <div className="story-progress-track flex-1 mr-3 !h-1">
            <div
              className="story-progress-fill transition-all duration-300"
              style={{ width: `${Math.min((charCount / MIN_CHARS) * 100, 100)}%`, opacity: isReady ? 1 : 0.6 }}
            />
          </div>
          <span className="text-xs text-[#A09890] dark:text-[#5C5650] shrink-0">
            {isReady ? 'Ready' : `${MIN_CHARS - charCount} more`}
          </span>
        </div>

        {/* Voice + hint */}
        <div className="flex items-center justify-between">
          {canRecord ? (
            <button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isTranscribing}
              className={`flex items-center gap-1.5 text-sm transition-colors ${
                isRecording
                  ? 'text-red-500 dark:text-red-400'
                  : 'text-[#8C847A] dark:text-[#7C746A] hover:text-[#5C4D6B] dark:hover:text-[#B0A0C0]'
              }`}
            >
              {isRecording ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <MicOff className="w-4 h-4" />
                  Stop
                </>
              ) : (
                <>
                  <Mic className="w-4 h-4" />
                  {isTranscribing ? 'Transcribing…' : 'Record instead'}
                </>
              )}
            </button>
          ) : (
            <span />
          )}
          <p className="text-xs text-[#A09890] dark:text-[#5C5650]">
            You can edit after the preview.
          </p>
        </div>
      </div>

      {/* Sticky CTA — stays accessible when mobile keyboard is open */}
      <div className="shrink-0 px-6 pb-6 pt-3 safe-area-bottom max-w-md mx-auto w-full border-t border-[var(--story-line)] bg-[var(--story-bg)]">
        <Button
          onClick={handleSubmit}
          disabled={!isReady}
          className={`w-full rounded-full py-6 font-medium transition-all duration-300 ${
            isReady
              ? 'story-button-primary text-white'
              : 'bg-[#D8D0C4] dark:bg-[#2A2520] text-[#A09890] dark:text-[#5C5650] cursor-not-allowed'
          }`}
        >
          Shape this memory
        </Button>
      </div>
    </div>
  );
}
